/**
 * @file ContractDrawerHeaderContainer.tsx
 * @description Derives avatar initials from the player name and binds
 *   the Profile link to an expo-router push for the player page. The
 *   container intentionally does NOT load identity data itself — the
 *   upstream drawer/page container already has the best view of the
 *   player and forwards the pre-resolved `metadataLine` so this module
 *   stays small and side-effect-free.
 * @module features/contracts/components/ContractDrawerHeader/ContractDrawerHeaderContainer
 */

import { router } from "expo-router";
import React, { memo, useCallback } from "react";

import ContractDrawerHeaderView, {
  type ContractDrawerHeaderVariant,
} from "./ContractDrawerHeaderView";

import { useNavigationContext } from "@/providers/NavigationProvider";

/** Props for {@link ContractDrawerHeaderContainer}. */
export interface ContractDrawerHeaderContainerProps {
  /** Which surface the header is rendered on. */
  readonly variant: ContractDrawerHeaderVariant;
  /** Player UUID used to build the Profile deep-link. */
  readonly playerId: string | null;
  /** Player display name (may be null while identity is resolving). */
  readonly playerName: string | null;
  /**
   * Optional metadata line below the name (e.g.
   * "Portugal · First Team · CAM · 31 Years"). When omitted the header
   * renders only the name — the container never fabricates metadata.
   */
  readonly metadataLine?: string;
  /** Whether the drawer is allowed to close right now. */
  readonly canClose: boolean;
  /** Invoked when the user taps the close X (drawer variant). */
  readonly onClose: () => void;
  /** Invoked when the user taps the back-chevron (page variant). */
  readonly onBack: () => void;
}

/**
 * Derives initials from a player's display name: first letter of the
 * first whitespace-delimited token + first letter of the last token,
 * uppercased. Single-token names return the first two characters of
 * that token. Falls back to "?" when the input is null, empty, or
 * whitespace-only so the Avatar fallback always renders something.
 * @param playerName - Display name, may be null
 * @returns The initials (always two characters at most)
 */
export function derivePlayerInitials(playerName: string | null): string {
  if (playerName === null) return "?";
  const parts = playerName
    .trim()
    .split(/\s+/)
    .filter(part => part.length > 0);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

/**
 * Stages a manual `history.pushState` of the current pathname (web only)
 * and invokes the drawer-dismiss callback, returning whether caller
 * should proceed with the `router.push` that follows.
 *
 * SE-4347 AC-5 (follow-up T1 + T3):
 * - **T1**: the drawer overlay must dismiss BEFORE the profile push so
 *   the player page doesn't land under a lingering drawer backdrop.
 *   `canClose` gates this — when the user has unsaved dirty edits the
 *   upstream `onClose` (aliased to `handleCloseRequest`) no-ops; if
 *   navigation proceeded anyway the drawer would stay mounted AND
 *   edits would silently vanish. Returning `false` from here short-
 *   circuits the whole flow so the dirty-edit path routes through the
 *   Cancel → discard-edits dialog instead.
 * - **T3**: on web the subsequent `router.push('/players/[id]')` is
 *   silently downgraded to `JUMP_TO` by Expo Router's dispatcher
 *   because `/squad` and `/players/[id]` live in the same `(tabs)`
 *   group (see `node_modules/expo-router/build/global-state/routing
 *   .js` — `type === 'PUSH' && navigationState.type !== 'stack'`
 *   promotes to NAVIGATE; the subsequent `expo-tab` branch promotes
 *   to JUMP_TO, which compiles to `history.replaceState`). That
 *   clobbers `/squad` from browser history so Back from the player
 *   page jumps past it. Staging an explicit `history.pushState` here
 *   puts a stable entry in the stack that JUMP_TO overwrites
 *   instead. Empirical trace on dev bundle `b5945aed…` captured by
 *   verification-specialist pins the behaviour precisely.
 *
 * Native platforms don't have `window.history`; the conditional
 * keeps this inert on iOS/Android where tab navigation pushes
 * correctly at the native layer anyway.
 * @param canClose - Whether the drawer is allowed to close right now
 * @param onClose - Drawer-dismiss callback (aliased to handleCloseRequest)
 * @returns `true` if the caller should proceed with `router.push`
 */
function dismissDrawerForProfileNav(
  canClose: boolean,
  onClose: () => void
): boolean {
  if (!canClose) return false;
  onClose();
  if (typeof window !== "undefined" && window.history?.pushState) {
    window.history.pushState(
      {},
      "",
      window.location.pathname + window.location.search
    );
  }
  return true;
}

/**
 * Container for the contract drawer/page header.
 * @param props - Container props
 * @param props.variant - Drawer or page chrome
 * @param props.playerId - Player UUID (drives Profile deep-link)
 * @param props.playerName - Player display name (drives initials + header text)
 * @param props.metadataLine - Optional metadata line under the name
 * @param props.canClose - Whether close is currently allowed
 * @param props.onClose - Invoked on close X tap
 * @param props.onBack - Invoked on back-chevron tap
 * @returns The rendered header bound to the Profile push
 */
function ContractDrawerHeaderContainer({
  variant,
  playerId,
  playerName,
  metadataLine = "",
  canClose,
  onClose,
  onBack,
}: ContractDrawerHeaderContainerProps) {
  const { pushBreadcrumb, clearBreadcrumbs } = useNavigationContext();

  const handleProfilePress = useCallback(() => {
    if (playerId === null) return;
    if (variant === "drawer" && !dismissDrawerForProfileNav(canClose, onClose))
      return;
    clearBreadcrumbs();
    pushBreadcrumb({ label: "Squad", path: "/squad" });
    pushBreadcrumb({
      label: playerName ?? "Player",
      path: `/players/${playerId}`,
    });
    router.push(`/players/${playerId}`);
  }, [
    canClose,
    playerId,
    variant,
    onClose,
    playerName,
    clearBreadcrumbs,
    pushBreadcrumb,
  ]);

  return (
    <ContractDrawerHeaderView
      variant={variant}
      playerName={playerName ?? ""}
      initials={derivePlayerInitials(playerName)}
      metadataLine={metadataLine}
      onClosePress={onClose}
      onBackPress={onBack}
      onProfilePress={handleProfilePress}
      canClose={canClose}
      isProfileEnabled={playerId !== null}
    />
  );
}

ContractDrawerHeaderContainer.displayName = "ContractDrawerHeaderContainer";

export default memo(ContractDrawerHeaderContainer);
